import { cn } from "@/lib/utils";

/**
 * Skeleton Design System
 * ──────────────────────
 * Default:   pulse animation with muted background
 * Shimmer:   gradient sweep for premium loading feel
 * Variants:  text, avatar, card, button for contextual shapes
 */

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "shimmer";
}

function Skeleton({ className, variant = "default", ...props }: SkeletonProps) {
  return (
    <div
      className={cn(
        "rounded-md bg-muted",
        variant === "default" && "animate-pulse",
        variant === "shimmer" &&
          "bg-gradient-to-r from-muted via-muted-foreground/5 to-muted bg-[length:200%_100%] animate-shimmer",
        className,
      )}
      {...props}
    />
  );
}

/** Pre-composed skeleton patterns for common layouts */
function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="shimmer"
          className={cn("h-4", i === lines - 1 ? "w-3/4" : "w-full")}
        />
      ))}
    </div>
  );
}

function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn("rounded-xl border bg-card p-6 space-y-4", className)}>
      <div className="flex items-center gap-3">
        <Skeleton variant="shimmer" className="h-10 w-10 rounded-full" />
        <div className="space-y-2 flex-1">
          <Skeleton variant="shimmer" className="h-4 w-1/3" />
          <Skeleton variant="shimmer" className="h-3 w-1/2" />
        </div>
      </div>
      <SkeletonText lines={3} />
    </div>
  );
}

export { Skeleton, SkeletonText, SkeletonCard };
